import {
  cellKey,
  EMPTY_IDS,
  type Card,
  type Column,
  type ColumnGroup,
  type NormalizedBoard,
  type PromptLocation,
  type Row,
  type Swimlane,
} from '@blueprint/domain'
import { useMemo } from 'react'
import { useBoardStore } from './board-store.js'

/**
 * Narrow selectors: a component subscribes to one entity or one ordered id list, so
 * editing a card cannot rerender unrelated cells.
 */

export function useColumnIds(): readonly string[] {
  return useBoardStore((state) => state.entities?.columnIds ?? EMPTY_IDS)
}

export function useSwimlaneIds(): readonly string[] {
  return useBoardStore((state) => state.entities?.swimlaneIds ?? EMPTY_IDS)
}

export function useRowIds(swimlaneId: string): readonly string[] {
  return useBoardStore((state) => state.entities?.rowIdsBySwimlane[swimlaneId] ?? EMPTY_IDS)
}

export function useCardIds(rowId: string, columnId: string): readonly string[] {
  return useBoardStore((state) => state.entities?.cardIdsByCell[cellKey(rowId, columnId)] ?? EMPTY_IDS)
}

export function useColumn(columnId: string): Column | undefined {
  return useBoardStore((state) => state.entities?.columns[columnId])
}

export function useSwimlane(swimlaneId: string): Swimlane | undefined {
  return useBoardStore((state) => state.entities?.swimlanes[swimlaneId])
}

export function useRow(rowId: string): Row | undefined {
  return useBoardStore((state) => state.entities?.rows[rowId])
}

export function useCard(cardId: string): Card | undefined {
  return useBoardStore((state) => state.entities?.cards[cardId])
}

export function useGroup(groupId: string | null): ColumnGroup | undefined {
  return useBoardStore((state) => (groupId ? state.entities?.groups[groupId] : undefined))
}

export function useGroupIds(): readonly string[] {
  return useBoardStore((state) => state.entities?.groupIds ?? EMPTY_IDS)
}

export function useBoardName(): string {
  return useBoardStore((state) => state.entities?.board.name ?? '')
}

export function useCanUndo(): boolean {
  return useBoardStore((state) => state.past.length > 0)
}

export function useCanRedo(): boolean {
  return useBoardStore((state) => state.future.length > 0)
}

export function useUndoLabel(): string | null {
  return useBoardStore((state) => state.past.at(-1)?.label ?? null)
}

export function useRedoLabel(): string | null {
  return useBoardStore((state) => state.future.at(-1)?.label ?? null)
}

/**
 * Where a card sits, as labels. The board is not a board -> epic -> story tree: a card
 * occupies a column (which may belong to a group) and a row (which belongs to a
 * swimlane). These are location labels only; no precedence is inferred from them.
 */
export function cardLocation(state: NormalizedBoard, card: Pick<Card, 'columnId' | 'rowId'>): PromptLocation {
  const column = state.columns[card.columnId]
  const row = state.rows[card.rowId]
  const swimlane = row ? state.swimlanes[row.swimlaneId] : undefined
  const group = column?.groupId ? state.groups[column.groupId] : undefined
  return {
    boardName: state.board.name,
    columnGroup: group?.title ?? null,
    column: column?.title ?? '',
    swimlane: swimlane?.title ?? '',
    row: row?.title ?? '',
  }
}

// Deliberately not a store hook: this builds a new object, which a reference-equality
// selector would treat as a change on every render. Call it inside a useMemo instead.

export interface EstimateTotals {
  /** Minutes per column id and per row id; absent means no card there gave an estimate. */
  byColumn: Record<string, number>
  byRow: Record<string, number>
}

const NO_ESTIMATES: EstimateTotals = { byColumn: {}, byRow: {} }

/**
 * Estimate minutes summed per column and per row, in one pass over the board rather than
 * once per heading — a 2,000-card board has 30 headings, and each would otherwise walk
 * every cell. Cards with no estimate contribute nothing, and a column or row where
 * nothing is estimated gets no entry at all, so headings can stay silent.
 *
 * Filters are deliberately ignored: the heading reports the size of the work, not the
 * size of the current view.
 */
export function useEstimateTotals(): EstimateTotals {
  const entities = useBoardStore((state) => state.entities)
  return useMemo(() => {
    if (!entities) return NO_ESTIMATES
    const byColumn: Record<string, number> = {}
    const byRow: Record<string, number> = {}
    for (const [key, cardIds] of Object.entries(entities.cardIdsByCell)) {
      const [rowId = '', columnId = ''] = key.split('|')
      for (const cardId of cardIds) {
        const minutes = entities.cards[cardId]?.estimateMinutes
        if (!minutes) continue
        byColumn[columnId] = (byColumn[columnId] ?? 0) + minutes
        byRow[rowId] = (byRow[rowId] ?? 0) + minutes
      }
    }
    return { byColumn, byRow }
  }, [entities])
}

/** `90` becomes `1h 30m`. Shared by the card and by the column and row headings. */
export function formatMinutes(minutes: number | null): string {
  if (minutes === null || minutes === 0) return ''
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
}

/**
 * Open findings about one element. Subscribes to the findings map only, which keeps its
 * identity until a finding changes, so cards do not rerender for unrelated edits.
 */
export function useOpenFindingCount(targetId: string): number {
  const findings = useBoardStore((state) => state.entities?.findings)
  return useMemo(() => {
    if (!findings) return 0
    let count = 0
    for (const finding of Object.values(findings)) if (finding.status === 'open' && finding.targetId === targetId) count += 1
    return count
  }, [findings, targetId])
}
